(() => {
  const SORT_VALUE = 'most_reviews';
  let activeSortPromise = null;
  let rerunAfterSort = false;

  function isMostReviews() {
    return new URL(window.location.href).searchParams.get('sort_by') === SORT_VALUE;
  }

  function getProductGrid() {
    return document.querySelector('#ProductGridContainer #product-grid');
  }

  function getSignature() {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.sort();
    return url.pathname + '?' + url.searchParams.toString();
  }

  function syncSortSelects() {
    if (!isMostReviews()) return;
    document.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      if (Array.from(select.options).some((option) => option.value === SORT_VALUE)) {
        select.value = SORT_VALUE;
      }
    });
  }

  function productCount(item) {
    const value = item.querySelector('product-component[data-most-reviews]')?.getAttribute('data-most-reviews');
    const parsed = Number.parseInt(value || '0', 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function productItems(grid) {
    return Array.from(grid.children).filter((item) =>
      item.matches('li.grid__item') && item.querySelector('product-component[data-most-reviews]')
    );
  }

  function updatePaginationLinks() {
    document.querySelectorAll('#ProductGridContainer .pagination a[href]').forEach((anchor) => {
      try {
        const url = new URL(anchor.getAttribute('href'), window.location.origin);
        url.searchParams.set('sort_by', SORT_VALUE);
        anchor.setAttribute('href', url.pathname + url.search + url.hash);
      } catch (error) {
        return;
      }
    });
  }

  async function fetchPageItems(page, sectionId) {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.delete('page');
    url.searchParams.delete('sort_by');
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Unable to load collection page ' + page + '.');

    const markup = await response.text();
    const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
    const parsedGrid = parsedDocument.querySelector('#ProductGridContainer #product-grid, #product-grid');
    if (!parsedGrid) throw new Error('The collection product grid was missing from the response.');

    return productItems(parsedGrid);
  }

  function sortVisibleItems(grid) {
    const items = productItems(grid);
    items
      .map((item, index) => ({ item, index, count: productCount(item) }))
      .sort((a, b) => b.count - a.count || a.index - b.index)
      .forEach(({ item }) => grid.appendChild(item));
    grid.dataset.mostReviewsSortedSignature = getSignature();
  }

  async function performSort() {
    if (!isMostReviews()) return;
    syncSortSelects();

    const grid = getProductGrid();
    if (!grid) return;

    const signature = getSignature();
    if (grid.dataset.mostReviewsSortedSignature === signature) {
      updatePaginationLinks();
      return;
    }

    grid.setAttribute('aria-busy', 'true');
    const totalPages = Math.max(1, Number.parseInt(grid.dataset.mostReviewTotalPages || '1', 10) || 1);
    const sectionId = grid.dataset.mostReviewSectionId || grid.dataset.id;
    const perPage = Math.max(1, Number.parseInt(grid.dataset.mostReviewPerPage || String(productItems(grid).length || 1), 10) || 1);

    try {
      if (!sectionId) throw new Error('Collection section id was unavailable.');

      const allPages = new Array(totalPages);
      let nextPage = 1;
      const workerCount = Math.min(4, totalPages);
      await Promise.all(Array.from({ length: workerCount }, async () => {
        while (true) {
          const page = nextPage;
          nextPage += 1;
          if (page > totalPages) break;
          allPages[page - 1] = await fetchPageItems(page, sectionId);
        }
      }));

      if (!isMostReviews()) return;
      const latestGrid = getProductGrid();
      if (!latestGrid) return;
      if (getSignature() !== signature) {
        rerunAfterSort = true;
        return;
      }

      const allItems = allPages.flat()
        .map((item, index) => ({ item, index, count: productCount(item) }))
        .sort((a, b) => b.count - a.count || a.index - b.index)
        .map(({ item }) => item);

      const currentUrl = new URL(window.location.href);
      const currentPage = Math.max(1, Number.parseInt(currentUrl.searchParams.get('page') || '1', 10) || 1);
      const start = (currentPage - 1) * perPage;
      latestGrid.replaceChildren(...allItems.slice(start, start + perPage));
      latestGrid.dataset.mostReviewsSortedSignature = signature;
      latestGrid.setAttribute('aria-busy', 'false');
      updatePaginationLinks();
    } catch (error) {
      const latestGrid = getProductGrid();
      if (latestGrid && isMostReviews()) {
        sortVisibleItems(latestGrid);
        latestGrid.setAttribute('aria-busy', 'false');
        updatePaginationLinks();
      }
      console.warn('Most Reviews sorting fell back to the products on this page.', error);
    }
  }

  function scheduleSort() {
    if (!isMostReviews()) return;
    if (activeSortPromise) {
      rerunAfterSort = true;
      return;
    }

    activeSortPromise = performSort().finally(() => {
      activeSortPromise = null;
      if (rerunAfterSort && isMostReviews()) {
        rerunAfterSort = false;
        scheduleSort();
      }
    });
  }

  function interceptCustomSortInput(event) {
    const select = event.target.closest?.('select[name="sort_by"]');
    if (!select || select.value !== SORT_VALUE) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  function onSortChange(event) {
    const select = event.target.closest?.('select[name="sort_by"]');
    if (!select || select.value !== SORT_VALUE) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', SORT_VALUE);
    url.searchParams.delete('page');
    const state = { ...(window.history.state || {}), searchParams: url.searchParams.toString() };
    window.history.pushState(state, '', url.pathname + url.search + url.hash);
    syncSortSelects();
    scheduleSort();
  }

  function init() {
    document.addEventListener('input', interceptCustomSortInput, true);
    document.addEventListener('change', onSortChange, true);

    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      new MutationObserver(() => {
        if (isMostReviews()) scheduleSort();
      }).observe(gridContainer, { childList: true });
    }

    window.addEventListener('popstate', () => {
      if (isMostReviews()) {
        syncSortSelects();
        scheduleSort();
      }
    });

    if (isMostReviews()) scheduleSort();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();